import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from '../auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from '../components/page-elements';

const STATUS_COLUMNS = ['On-Spot', 'Available', 'Inbound', 'Released'];

const formatCount = (value) => Number(value || 0).toLocaleString();

const Railcars = () => {
  const user = useMemo(() => getStoredUser(), []);
  const navigate = useNavigate();
  const isCustomer = user?.role === 'customer';
  const isInternal = user?.role === 'internal' || user?.role === 'admin';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [railcars, setRailcars] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('On-Spot');
  const [customerFilter, setCustomerFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedRailcarId, setSelectedRailcarId] = useState('');
  const [associatedTruckBols, setAssociatedTruckBols] = useState([]);
  const [associatedTruckBolsLoading, setAssociatedTruckBolsLoading] = useState(false);
  const [currentOrders, setCurrentOrders] = useState([]);
  const [currentOrdersLoading, setCurrentOrdersLoading] = useState(false);

  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseConfirmStep, setReleaseConfirmStep] = useState(1);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [requestingPlacement, setRequestingPlacement] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsForm, setDetailsForm] = useState({ materialName: '', batchNumber: '', railcarBolNumber: '' });

  const fetchRailcars = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/railcars`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch railcars');
      const data = await response.json();
      setRailcars(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRailcars();
  }, []);

  useEffect(() => {
    if (!isInternal) return;
    setMaterialsLoading(true);
    fetch(`${API_BASE_URL}/api/materials`, { headers: getAuthHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load materials');
        return res.json();
      })
      .then((rows) => setMaterials(Array.isArray(rows) ? rows.filter((m) => m.isActive !== false) : []))
      .catch(() => setMaterials([]))
      .finally(() => setMaterialsLoading(false));
  }, [isInternal]);

  const counts = useMemo(() => {
    const summary = { 'On-Spot': 0, Available: 0, Inbound: 0, Released: 0 };
    railcars.forEach((railcar) => {
      const status = STATUS_COLUMNS.includes(railcar.currentStatus) ? railcar.currentStatus : 'Inbound';
      summary[status] += 1;
    });
    return summary;
  }, [railcars]);

  const filteredRailcars = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    return railcars.filter((railcar) => {
      const statusOk = statusFilter === 'All' ? true : railcar.currentStatus === statusFilter;
      if (!statusOk) return false;
      const customerId = String(railcar.customerName?._id || railcar.customerName || '');
      const customerOk = !isInternal || customerFilter === 'All' ? true : customerId === customerFilter;
      if (!customerOk) return false;
      if (!searchTerm) return true;
      const haystack = [
        railcar.railcarID,
        railcar.carInitial,
        railcar.carNumber,
        railcar.station,
        railcar.track,
        railcar.trackPosition,
        railcar.materialName?.materialName,
        railcar.batchNumber,
      ]
        .map((v) => String(v || '').toLowerCase())
        .join(' ');
      return haystack.includes(searchTerm);
    });
  }, [railcars, statusFilter, search, isInternal, customerFilter]);

  const customerFilterOptions = useMemo(() => {
    const map = new Map();
    railcars.forEach((railcar) => {
      const id = String(railcar.customerName?._id || railcar.customerName || '').trim();
      const name =
        railcar.customerName?.customerName ||
        railcar.customerName?.name ||
        (typeof railcar.customerName === 'string' ? railcar.customerName : '');
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, name || id);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }));
  }, [railcars]);

  useEffect(() => {
    if (filteredRailcars.length === 0) {
      setSelectedRailcarId('');
      return;
    }
    const exists = filteredRailcars.some((r) => r._id === selectedRailcarId);
    if (!exists) {
      setSelectedRailcarId(filteredRailcars[0]._id);
    }
  }, [filteredRailcars, selectedRailcarId]);

  const selectedRailcar = useMemo(
    () => filteredRailcars.find((railcar) => railcar._id === selectedRailcarId) || null,
    [filteredRailcars, selectedRailcarId]
  );

  useEffect(() => {
    if (!selectedRailcar?.railcarID) {
      setAssociatedTruckBols([]);
      return;
    }

    const customerId = String(selectedRailcar?.customerName?._id || selectedRailcar?.customerName || '').trim();
    const params = new URLSearchParams({
      railcarID: String(selectedRailcar.railcarID),
    });
    if (customerId) params.set('customerId', customerId);
    if (selectedRailcar?.railcarBolNumber) {
      params.set('railShipmentBolNumber', String(selectedRailcar.railcarBolNumber));
    }

    setAssociatedTruckBolsLoading(true);
    fetch(`${API_BASE_URL}/api/bols?${params.toString()}`, { headers: getAuthHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load related truck BOLs');
        return res.json();
      })
      .then((rows) => setAssociatedTruckBols(Array.isArray(rows) ? rows : []))
      .catch(() => setAssociatedTruckBols([]))
      .finally(() => setAssociatedTruckBolsLoading(false));
  }, [selectedRailcar]);

  useEffect(() => {
    if (!selectedRailcar?.railcarID) {
      setCurrentOrders([]);
      return;
    }

    const customerId = String(selectedRailcar?.customerName?._id || selectedRailcar?.customerName || '').trim();
    const params = new URLSearchParams();
    if (customerId) params.set('customerId', customerId);

    setCurrentOrdersLoading(true);
    fetch(`${API_BASE_URL}/api/orders${params.toString() ? `?${params.toString()}` : ''}`, { headers: getAuthHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load related orders');
        return res.json();
      })
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        const filtered = list.filter((order) => {
          const status = String(order?.orderStatus || 'Draft');
          if (status === 'Cancelled' || status === 'Completed' || status === 'Shipped') return false;
          return (
            String(order?.railcarID || '').trim() === String(selectedRailcar.railcarID).trim() ||
            String(order?.secondaryRailcarID || '').trim() === String(selectedRailcar.railcarID).trim()
          );
        });
        setCurrentOrders(filtered);
      })
      .catch(() => setCurrentOrders([]))
      .finally(() => setCurrentOrdersLoading(false));
  }, [selectedRailcar]);

  const openReleaseDialog = (railcar) => {
    setReleaseTarget(railcar);
    setReleaseConfirmStep(1);
    setReleaseDialogOpen(true);
  };

  const closeReleaseDialog = () => {
    setReleaseDialogOpen(false);
    setReleaseTarget(null);
    setReleaseConfirmStep(1);
  };

  const confirmRelease = async () => {
    if (!releaseTarget?._id) return;
    if (releaseConfirmStep === 1) {
      setReleaseConfirmStep(2);
      return;
    }

    setReleasing(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/railcars/${releaseTarget._id}/release-empty`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to release railcar');
      }
      closeReleaseDialog();
      await fetchRailcars();
    } catch (err) {
      setError(err.message);
    }
    setReleasing(false);
  };

  const requestPlacement = async (railcar) => {
    if (!railcar?._id) return;
    const confirmed = window.confirm(`Request placement for ${railcar.railcarID}?`);
    if (!confirmed) return;

    setRequestingPlacement(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/railcars/${railcar._id}/request-placement`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to request placement');
      }
      await fetchRailcars();
    } catch (err) {
      setError(err.message);
    }
    setRequestingPlacement(false);
  };

  const openDetailsDialog = (railcar) => {
    if (!railcar) return;
    setDetailsForm({
      materialName: railcar.materialName?._id || railcar.materialName || '',
      batchNumber: railcar.batchNumber || '',
      railcarBolNumber: railcar.railcarBolNumber || '',
    });
    setSelectedRailcarId(railcar._id);
    setDetailsDialogOpen(true);
  };

  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setDetailsForm({ materialName: '', batchNumber: '', railcarBolNumber: '' });
  };

  const saveRailcarDetails = async () => {
    if (!selectedRailcar?._id) return;
    setDetailsSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/railcars/${selectedRailcar._id}/details`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          materialName: detailsForm.materialName || null,
          batchNumber: detailsForm.batchNumber || '',
          railcarBolNumber: detailsForm.railcarBolNumber || '',
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to update railcar details');
      }
      closeDetailsDialog();
      await fetchRailcars();
    } catch (err) {
      setError(err.message);
    }
    setDetailsSaving(false);
  };

  const materialOptions = useMemo(() => {
    const customerId = String(selectedRailcar?.customerName?._id || selectedRailcar?.customerName || '');
    return materials.filter((material) => {
      const materialCustomerId = String(material?.customerName?._id || material?.customerName || '');
      return materialCustomerId && materialCustomerId === customerId;
    });
  }, [materials, selectedRailcar]);

  if (loading) return <LoadingPane label="Loading railcars..." />;

  const downloadOnlineReport = async () => {
    setDownloadingReport(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/railcars/report?onlineOnly=true`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || 'Failed to download railcar report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const now = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `railcars-online-report-${now}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
    setDownloadingReport(false);
  };

  return (
    <Box p={2}>
      <PageHeader
        title="Railcars"
        subtitle="Focus on on-spot and available railcars with actionable detail."
        actions={
          <>
            {isCustomer ? (
              <Button variant="outlined" onClick={downloadOnlineReport} disabled={downloadingReport}>
                {downloadingReport ? 'Preparing Report...' : 'Download Online Railcar Report'}
              </Button>
            ) : null}
            {isInternal ? (
              <Button variant="outlined" onClick={() => navigate('/railcars/upload')}>
                Upload CSV
              </Button>
            ) : null}
          </>
        }
      />
      <ErrorBanner message={error} />

      <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
        <Grid item xs={3}>
          <Card variant="outlined" className="surface-hover"><CardContent sx={{ py: 1.2 }}><Typography variant="caption" color="text.secondary">On-Spot</Typography><Typography variant="h5" fontWeight={700}>{formatCount(counts['On-Spot'])}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={3}>
          <Card variant="outlined" className="surface-hover"><CardContent sx={{ py: 1.2 }}><Typography variant="caption" color="text.secondary">Available</Typography><Typography variant="h5" fontWeight={700}>{formatCount(counts.Available)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={3}>
          <Card variant="outlined" className="surface-hover"><CardContent sx={{ py: 1.2 }}><Typography variant="caption" color="text.secondary">Inbound</Typography><Typography variant="h5" fontWeight={700}>{formatCount(counts.Inbound)}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={3}>
          <Card variant="outlined" className="surface-hover"><CardContent sx={{ py: 1.2 }}><Typography variant="caption" color="text.secondary">Released</Typography><Typography variant="h5" fontWeight={700}>{formatCount(counts.Released)}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={8.5}>
          <Paper elevation={0} className="surface-hover" sx={{ borderRadius: 2, border: '1px solid var(--ui-border)', overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, pt: 1 }}>
              <Tabs
                value={statusFilter}
                onChange={(_, value) => setStatusFilter(value)}
                variant={isMobile ? 'scrollable' : 'standard'}
              >
                <Tab label={`On-Spot (${counts['On-Spot']})`} value="On-Spot" />
                <Tab label={`Available (${counts.Available})`} value="Available" />
                <Tab label={`Inbound (${counts.Inbound})`} value="Inbound" />
                <Tab label={`Released (${counts.Released})`} value="Released" />
                <Tab label={`All (${railcars.length})`} value="All" />
              </Tabs>
              {isInternal && (
                <FormControl fullWidth size="small" margin="normal">
                  <InputLabel id="railcar-customer-filter-label">Customer</InputLabel>
                  <Select
                    labelId="railcar-customer-filter-label"
                    label="Customer"
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value)}
                  >
                    <MenuItem value="All">All Customers</MenuItem>
                    {customerFilterOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                fullWidth
                size="small"
                margin="normal"
                label="Search railcars"
                placeholder="Railcar, station, track, material, batch"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
            <DividerLine />
            {filteredRailcars.length === 0 ? (
              <Box p={2}>
                <EmptyPane title="No railcars for this view" subtitle="Try another status tab or search term." />
              </Box>
            ) : (
              <Box sx={{ maxHeight: isMobile ? 'unset' : 560, overflowY: 'auto' }}>
                {filteredRailcars.map((railcar) => {
                  const selected = railcar._id === selectedRailcarId;
                  const placementRequested = Boolean(railcar.placementRequestedAt);
                  const canReleaseAsEmpty = (isCustomer || isInternal) && (railcar.currentStatus === 'Available' || railcar.currentStatus === 'On-Spot');
                  return (
                    <Box
                      key={railcar._id}
                      onClick={() => setSelectedRailcarId(railcar._id)}
                      sx={{
                        px: 1.5,
                        py: 1.1,
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        backgroundColor: selected ? '#eff6ff' : 'transparent',
                        '&:hover': { backgroundColor: selected ? '#dbeafe' : '#ffffff' },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Box>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography fontWeight={700}>{railcar.railcarID}</Typography>
                            <Chip
                              size="small"
                              label={railcar.currentStatus}
                              color={
                                railcar.currentStatus === 'On-Spot'
                                  ? 'success'
                                  : railcar.currentStatus === 'Available'
                                  ? 'info'
                                  : railcar.currentStatus === 'Released'
                                  ? 'default'
                                  : 'warning'
                              }
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {`Station: ${railcar.station || 'N/A'} | Track: ${railcar.track || 'N/A'} | Position: ${railcar.trackPosition || 'N/A'} | Remaining: ${
                              railcar.remainingWeight != null ? Number(railcar.remainingWeight).toLocaleString() : 'N/A'
                            }`}
                          </Typography>
                          {(railcar.materialName?.materialName || railcar.batchNumber) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {`Material: ${railcar.materialName?.materialName || 'N/A'} | Batch: ${railcar.batchNumber || 'N/A'}`}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {isInternal && (
                            <Button
                              size="small"
                              color="secondary"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetailsDialog(railcar);
                              }}
                            >
                              Add Details
                            </Button>
                          )}
                          {canReleaseAsEmpty && (
                            <Button
                              size="small"
                              color="warning"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                openReleaseDialog(railcar);
                              }}
                            >
                              Release as Empty
                            </Button>
                          )}
                          {isCustomer && railcar.currentStatus !== 'On-Spot' && railcar.currentStatus !== 'Released' && (
                            <Button
                              size="small"
                              color={placementRequested ? 'default' : 'info'}
                              variant={placementRequested ? 'contained' : 'outlined'}
                              disabled={requestingPlacement || placementRequested}
                              onClick={(e) => {
                                e.stopPropagation();
                                requestPlacement(railcar);
                              }}
                            >
                              {placementRequested ? 'Placement Requested' : 'Request Placement'}
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={3.5}>
          <Stack spacing={1.2} sx={{ position: isMobile ? 'static' : 'sticky', top: 16 }}>
            <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    p: 1.25,
                    borderBottom: '1px solid #e2e8f0',
                    background: 'linear-gradient(135deg, #ece7df 0%, #ffffff 100%)',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={800}>
                    Railcar Detail
                  </Typography>
                  {selectedRailcar?.railcarID ? (
                    <Typography variant="caption" color="text.secondary">
                      {selectedRailcar.railcarID}
                    </Typography>
                  ) : null}
                </Box>
                <Box sx={{ p: 1.25 }}>
                {!selectedRailcar ? (
                  <Typography variant="body2" color="text.secondary">Select a railcar to view details.</Typography>
                ) : (
                  <Stack spacing={0.7}>
                    <DetailRow label="Status" value={selectedRailcar.currentStatus} />
                    <DetailRow label="Commodity" value={selectedRailcar.commodity || 'N/A'} />
                    <DetailRow label="Station" value={selectedRailcar.station || 'N/A'} />
                    <DetailRow label="Track" value={selectedRailcar.track || 'N/A'} />
                    <DetailRow label="Track Position" value={selectedRailcar.trackPosition || 'N/A'} />
                    <DetailRow label="Remaining Weight" value={selectedRailcar.remainingWeight != null ? Number(selectedRailcar.remainingWeight).toLocaleString() : 'N/A'} />
                    <DetailRow label="Rail Shipment BOL" value={selectedRailcar.railcarBolNumber || 'N/A'} />
                    <DetailRow label="Material" value={selectedRailcar.materialName?.materialName || 'N/A'} />
                    <DetailRow label="Batch Number" value={selectedRailcar.batchNumber || 'N/A'} />
                    <Stack direction="row" spacing={0.7} sx={{ pt: 0.6 }}>
                      {isInternal && (
                        <Button size="small" variant="outlined" onClick={() => openDetailsDialog(selectedRailcar)}>
                          Add Details
                        </Button>
                      )}
                      {((isCustomer || isInternal) && (selectedRailcar.currentStatus === 'Available' || selectedRailcar.currentStatus === 'On-Spot')) && (
                        <Button size="small" color="warning" variant="contained" onClick={() => openReleaseDialog(selectedRailcar)}>
                          Release as Empty
                        </Button>
                      )}
                      {isCustomer && selectedRailcar.currentStatus !== 'On-Spot' && selectedRailcar.currentStatus !== 'Released' && (
                        <Button
                          size="small"
                          color={selectedRailcar.placementRequestedAt ? 'default' : 'info'}
                          variant={selectedRailcar.placementRequestedAt ? 'contained' : 'outlined'}
                          disabled={requestingPlacement || Boolean(selectedRailcar.placementRequestedAt)}
                          onClick={() => requestPlacement(selectedRailcar)}
                        >
                          {selectedRailcar.placementRequestedAt ? 'Placement Requested' : 'Request Placement'}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                )}
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.6 }}>
                  Truck BOL Shipments
                </Typography>
                {associatedTruckBolsLoading ? (
                  <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={18} />
                  </Box>
                ) : associatedTruckBols.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No truck BOLs associated.</Typography>
                ) : (
                  <Stack spacing={0.7}>
                    {associatedTruckBols.slice(0, 6).map((bol) => (
                      <Box key={bol._id} sx={{ borderBottom: '1px dashed var(--ui-border)', pb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {bol.orderNumber?.orderNumber || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`Status: ${bol.status || 'Draft'} | Truck: ${bol.truckID || 'N/A'} | Net: ${bol.netWeight || 'N/A'}`}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.6 }}>
                  Current Orders
                </Typography>
                {currentOrdersLoading ? (
                  <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={18} />
                  </Box>
                ) : currentOrders.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No active orders for this railcar.</Typography>
                ) : (
                  currentOrders.slice(0, 6).map((order) => (
                    <Box key={order._id} sx={{ borderBottom: '1px dashed var(--ui-border)', pb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {order.orderNumber || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`Status: ${order.orderStatus || 'Draft'} | Material: ${order.materialName?.materialName || 'N/A'}`}
                      </Typography>
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={releaseDialogOpen} onClose={closeReleaseDialog} fullWidth maxWidth="xs">
        <DialogTitle>{releaseConfirmStep === 1 ? 'Confirm Release Request' : 'Final Confirmation Required'}</DialogTitle>
        <DialogContent>
          {releaseConfirmStep === 1 ? (
            <Typography>
              Release railcar {releaseTarget?.carInitial} {releaseTarget?.carNumber} as empty?
            </Typography>
          ) : (
            <Typography color="error">
              This action will move the railcar to Released status. Are you absolutely sure?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReleaseDialog}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={confirmRelease} disabled={releasing}>
            {releasing ? 'Releasing...' : releaseConfirmStep === 1 ? 'Yes, Continue' : 'Yes, Release'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsDialogOpen} onClose={closeDetailsDialog} fullWidth maxWidth="sm">
        <DialogTitle>Railcar Details</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Railcar: {selectedRailcar?.railcarID || 'N/A'}
          </Typography>
          <FormControl fullWidth margin="normal" disabled={materialsLoading}>
            <InputLabel id="railcar-material-label">Customer Material</InputLabel>
            <Select
              labelId="railcar-material-label"
              value={detailsForm.materialName}
              label="Customer Material"
              onChange={(e) => setDetailsForm((prev) => ({ ...prev, materialName: e.target.value }))}
            >
              <MenuItem value="">None</MenuItem>
              {materialOptions.map((material) => (
                <MenuItem key={material._id} value={material._id}>
                  {material.materialName} {material.refNum ? `(${material.refNum})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="normal"
            label="Batch Number"
            fullWidth
            value={detailsForm.batchNumber}
            onChange={(e) => setDetailsForm((prev) => ({ ...prev, batchNumber: e.target.value }))}
          />
          <TextField
            margin="normal"
            label="Railcar Shipment BOL Number"
            fullWidth
            value={detailsForm.railcarBolNumber || ''}
            onChange={(e) => setDetailsForm((prev) => ({ ...prev, railcarBolNumber: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailsDialog}>Cancel</Button>
          <Button variant="contained" onClick={saveRailcarDetails} disabled={detailsSaving}>
            {detailsSaving ? 'Saving...' : 'Save Details'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const DetailRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ py: 0.3 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 20 }}>{value}</Box>
    )}
  </Stack>
);

const DividerLine = () => <Box sx={{ height: 1, backgroundColor: 'var(--ui-border)' }} />;

export default Railcars;

